// src/pages/LoginPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Link,
  Alert,
  CircularProgress,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import { useAuth } from '../auth/CognitoAuthContext';
import keyspiderLogoWhite from '../assets/keyspider-logo-white.svg';

const BRAND_DARK = '#18212F';
const BRAND_BLUE = '#004AAD';

function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  // 'login' | 'new-password' | 'forgot-request' | 'forgot-confirm'
  const [view, setView] = useState('login');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetCode, setResetCode] = useState('');

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [infoMsg, setInfoMsg] = useState('');

  useEffect(() => {
    if (!auth.isLoading && auth.isAuthenticated) {
      navigate('/app', { replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  const resetMessages = () => {
    setErrorMsg('');
    setInfoMsg('');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    try {
      const result = await auth.signIn(email, password);
      if (result.status === 'NEW_PASSWORD_REQUIRED') {
        setView('new-password');
      } else {
        navigate('/app', { replace: true });
      }
    } catch (err) {
      setErrorMsg(err.message || 'Unable to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    resetMessages();
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await auth.completeNewPassword(newPassword);
      navigate('/app', { replace: true });
    } catch (err) {
      setErrorMsg(err.message || 'Unable to set new password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);
    try {
      await auth.forgotPassword(email);
      setInfoMsg('We sent a verification code to your email.');
      setView('forgot-confirm');
    } catch (err) {
      setErrorMsg(err.message || 'Unable to send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotConfirm = async (e) => {
    e.preventDefault();
    resetMessages();
    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await auth.confirmForgotPassword(email, resetCode, newPassword);
      setInfoMsg('Password reset. Please sign in with your new password.');
      setNewPassword('');
      setConfirmPassword('');
      setResetCode('');
      setView('login');
    } catch (err) {
      setErrorMsg(err.message || 'Unable to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    resetMessages();
    setView('login');
  };

  const goToForgot = () => {
    resetMessages();
    setView('forgot-request');
  };

  if (auth.isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={40} thickness={5} sx={{ color: BRAND_BLUE }} />
      </Box>
    );
  }

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': { borderColor: BRAND_BLUE },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND_BLUE },
  };

  const signInButtonSx = {
    backgroundColor: BRAND_BLUE,
    fontWeight: 600,
    borderRadius: '8px',
    height: 46,
    boxShadow: 'none',
    '&:hover': { backgroundColor: '#003A87', boxShadow: 'none' },
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Left brand panel */}
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: '260px', md: '100vh' },
          backgroundColor: BRAND_DARK,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: { xs: 4, md: 8 },
        }}
      >
        <Box>
          <img src={keyspiderLogoWhite} alt="Keyspider" style={{ height: 32, width: 'auto' }} />
        </Box>

        <Box sx={{ maxWidth: 480 }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            PDF Accessibility Remediation
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.75)' }}>
            Sign in to tag your PDFs, generate alt text, and bring them to
            WCAG 2.1 AA.
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          © {new Date().getFullYear()} Keyspider
        </Typography>
      </Box>

      {/* Right form panel */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#F5F6F8',
          padding: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          {view === 'login' && (
            <Box component="form" onSubmit={handleSignIn}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Welcome back
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Sign in to your account
              </Typography>

              {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
              {infoMsg && <Alert severity="success" sx={{ mb: 2 }}>{infoMsg}</Alert>}

              <TextField
                label="Email"
                type="email"
                autoComplete="username"
                fullWidth
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2, ...textFieldSx }}
              />
              <TextField
                label="Password"
                type="password"
                autoComplete="current-password"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 1, ...textFieldSx }}
              />

              <Box sx={{ textAlign: 'right', mb: 2 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={goToForgot}
                  underline="hover"
                  sx={{ fontSize: '0.875rem', color: BRAND_BLUE }}
                >
                  Forgot password?
                </Link>
              </Box>

              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={loading}
                sx={signInButtonSx}
              >
                Sign In
              </LoadingButton>
            </Box>
          )}

          {view === 'new-password' && (
            <Box component="form" onSubmit={handleSetNewPassword}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Set a new password
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Your account requires a new password before continuing.
              </Typography>

              {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                fullWidth
                required
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2, ...textFieldSx }}
              />
              <TextField
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 2, ...textFieldSx }}
              />

              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={loading}
                sx={signInButtonSx}
              >
                Set Password
              </LoadingButton>
            </Box>
          )}

          {view === 'forgot-request' && (
            <Box component="form" onSubmit={handleForgotRequest}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Reset your password
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Enter your email and we'll send you a verification code.
              </Typography>

              {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}

              <TextField
                label="Email"
                type="email"
                autoComplete="username"
                fullWidth
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2, ...textFieldSx }}
              />

              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={loading}
                sx={{ ...signInButtonSx, mb: 2 }}
              >
                Send reset code
              </LoadingButton>

              <Box sx={{ textAlign: 'center' }}>
                <Link component="button" type="button" onClick={goToLogin} underline="hover" sx={{ fontSize: '0.875rem', color: BRAND_BLUE }}>
                  Back to sign in
                </Link>
              </Box>
            </Box>
          )}

          {view === 'forgot-confirm' && (
            <Box component="form" onSubmit={handleForgotConfirm}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                Enter reset code
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                Check your email for the verification code.
              </Typography>

              {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
              {infoMsg && <Alert severity="success" sx={{ mb: 2 }}>{infoMsg}</Alert>}

              <TextField
                label="Verification code"
                autoComplete="one-time-code"
                fullWidth
                required
                autoFocus
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                sx={{ mb: 2, ...textFieldSx }}
              />
              <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                fullWidth
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={{ mb: 2, ...textFieldSx }}
              />
              <TextField
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                fullWidth
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                sx={{ mb: 2, ...textFieldSx }}
              />

              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                loading={loading}
                sx={{ ...signInButtonSx, mb: 2 }}
              >
                Reset Password
              </LoadingButton>

              <Box sx={{ textAlign: 'center' }}>
                <Link component="button" type="button" onClick={goToLogin} underline="hover" sx={{ fontSize: '0.875rem', color: BRAND_BLUE }}>
                  Back to sign in
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default LoginPage;
